import React, { useState, useEffect } from "react";
import { CreditCard, Banknote, HandCoins } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import LoginModal from "@/components/LoginModal";
import { pagesApi, type PageLink } from "@/services/pagesApi";

const linksCol1 = [
  "Location",
  "Devenir Revendeur",
  "Contrat de Maintenance",
  "Vérification de Garantie",
  "Support",
  "Contact",
  "Attestation de régularité fiscale",
];

const payments = [
  { key: "visa", label: "Visa", Icon: CreditCard },
  { key: "mastercard", label: "Mastercard", Icon: CreditCard },
  { key: "wire", label: "Wire Transfer", Icon: Banknote },
  { key: "cod", label: "Cash on Delivery", Icon: HandCoins },
];

function Footer() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [dynamicPages, setDynamicPages] = useState<PageLink[]>([]);
  const year = new Date().getFullYear();

  useEffect(() => {
    const fetchPages = async () => {
      try {
        const pagesData = await pagesApi.getFooterPages();
        // Filter to only show custom pages with cwp- slug prefix
        const customPages = pagesData.pages.filter(page => page.slug.startsWith('cwp-'));
        setDynamicPages(customPages);
      } catch (error) {
        console.error('Failed to fetch footer pages:', error);
      }
    };

    fetchPages();
  }, []);

  const handleSupportClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isAuthenticated) {
      navigate('/account?tab=claims');
    } else {
      setShowLoginModal(true);
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    navigate('/account?tab=claims');
  };
  return (
    <>
    <footer className="bg-primary text-primary-foreground" aria-label="Pied de page">
      <div className="container mx-auto px-4 py-10">
        <div className="grid gap-8 md:grid-cols-3">
          {/* Left: Logo and copy */}
          <div className="flex flex-col gap-4">
            <a href="/" className="inline-flex items-center gap-3 self-center" aria-label="Accueil">
              <img
                src="/lovable-uploads/d5b011bc-34ae-449a-8ec6-8e13ea03ceae.png"
                alt="Logo Mon Copieur"
                loading="lazy"
                className="h-16 md:h-20 w-auto select-none"
              />
              <span className="sr-only">Mon Copieur</span>
            </a>
          </div>

          {/* Right: Links in two columns */}
          <nav className="md:col-span-2" aria-label="Liens utiles">
            <div className="grid gap-2 sm:grid-cols-2">
                <ul>
                {linksCol1.map((label) => (
                  <li key={label}>
                     {label === "Support" ? (
                      <button
                        onClick={handleSupportClick}
                        className="inline-block py-1 transition-colors duration-200 hover:text-primary-foreground/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-foreground rounded text-left"
                      >
                        {label}
                      </button>
                    ) : label === "Attestation de régularité fiscale" ? (
                      <a
                        href="https://attestation.tax.gov.ma/verifAttest/verifyAttest.do?type=AttestationREGFRASTVA&ifu=66159799&annee=2025&numSerie=184fa4a2b896715b&method=verify"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block py-1 transition-colors duration-200 hover:text-primary-foreground/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-foreground rounded"
                      >
                        {label}
                      </a>
                    ) : (
                      <a
                        href={
                          label === "Contact"
                            ? "/contact"
                            : label === "Location"
                            ? "/location"
                            : label === "Devenir Revendeur"
                            ? "/devenir-revendeur"
                            : label === "Contrat de Maintenance"
                            ? "/contrat-de-maintenance"
                            : label === "Vérification de Garantie"
                            ? "/verification-garantie"
                            : "#"
                        }
                        className="inline-block py-1 transition-colors duration-200 hover:text-primary-foreground/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-foreground rounded"
                      >
                        {label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
              <ul>
                {/* Dynamic pages from API */}
                {dynamicPages.map((page) => (
                  <li key={page.slug}>
                    <a
                      href={`/${page.slug}`}
                      className="inline-block py-1 transition-colors duration-200 hover:text-primary-foreground/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-foreground rounded"
                    >
                      {page.name}
                    </a>
                  </li>
                ))}
                
              </ul>
            </div>
          </nav>
        </div>

        {/* Bottom white bar: copyright + payment methods */}
        <div className="mt-8 rounded-md border border-border bg-background text-foreground shadow-sm">
          <div className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between">
            <p className="text-sm">Copyright © Mon Copieur {year}. All rights reserved.</p>
            <div className="flex items-center gap-2">
              {payments.map(({ key, label, Icon }) => (
                <Tooltip key={key}>
                  <TooltipTrigger asChild>
                    <span
                      role="img"
                      aria-label={label}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:text-foreground"
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">{label}</TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>

    <LoginModal 
      isOpen={showLoginModal}
      onOpenChange={setShowLoginModal}
      onSuccess={handleLoginSuccess}
    />
    </>
  );
}

export default Footer;
